import { getSupabaseClient } from '../../lib/supabase'
import { getIntelligenceAdminWorkspace } from './contractor-intelligence-service'

export type ContractorVerificationStatus = 'draft' | 'pending' | 'verified' | 'rejected' | 'suspended'

export interface ContractorVerificationQueueItem {
  userId: string
  email: string
  businessName: string
  tradeSummary: string
  phone: string | null
  city: string
  region: string
  postalCode: string
  serviceRadiusMiles: number
  verificationStatus: ContractorVerificationStatus
  insuranceVerified: boolean
  backgroundCheckVerified: boolean
  submittedAt: string | null
  reviewedAt: string | null
  rejectionReason: string | null
  categories: Array<{ id: string; name: string }>
}

export interface ContractorVerificationHistoryItem {
  id: string
  contractorUserId: string
  businessName: string
  email: string
  previousStatus: ContractorVerificationStatus
  decision: 'verified' | 'rejected'
  reason: string | null
  insuranceVerified: boolean
  backgroundCheckVerified: boolean
  reviewedBy: string
  createdAt: string
}

type RpcResult<T> = PromiseLike<{ data: T | null; error: { message: string } | null }>

function invoke<T>(name: string, args?: Record<string, unknown>) {
  return getSupabaseClient().rpc(name as never, (args ?? {}) as never) as unknown as RpcResult<T>
}

export async function getContractorVerificationWorkspace() {
  const [queue, history, compliance, intelligence] = await Promise.all([
    invoke<ContractorVerificationQueueItem[]>('get_contractor_verification_queue'),
    invoke<ContractorVerificationHistoryItem[]>('get_contractor_verification_history'),
    invoke<ComplianceAdminWorkspace>('get_compliance_admin_workspace'),
    getIntelligenceAdminWorkspace(),
  ])

  if (queue.error) throw new Error(queue.error.message)
  if (history.error) throw new Error(history.error.message)
  if (compliance.error) throw new Error(compliance.error.message)

  return {
    queue: queue.data ?? [],
    history: history.data ?? [],
    compliance: compliance.data ?? { pending:[],expiring:[],backgroundReview:[],recentAudit:[] },
    intelligence,
  }
}

export type ComplianceQueueItem = { contractor_user_id:string;credential_type:string;credential_id:string|null;status:string;updated_at:string }
export type ComplianceAdminWorkspace = { pending:ComplianceQueueItem[];expiring:Array<Record<string,unknown>>;backgroundReview:Array<Record<string,unknown>>;recentAudit:Array<Record<string,unknown>> }

export async function reviewComplianceRecord(item:ComplianceQueueItem,decision:'verified'|'rejected'|'review_required',reason:string) {
  const { error }=await invoke<null>('review_compliance_record',{ target_user_id:item.contractor_user_id,record_type:item.credential_type,record_id:item.credential_id,target_status:decision,reason,notes:null })
  if (error) throw new Error(error.message)
}

export async function reviewContractorProfile(input: {
  userId: string
  decision: 'verified' | 'rejected'
  reason?: string
  insuranceVerified: boolean
  backgroundCheckVerified: boolean
}) {
  const { error } = await invoke<string>('review_contractor_profile', {
    target_user_id: input.userId,
    target_decision: input.decision,
    review_reason: input.reason?.trim() || null,
    insurance_check: input.insuranceVerified,
    background_check: input.backgroundCheckVerified,
  })

  if (error) throw new Error(error.message)
}
